import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import {
  FaHome,
  FaLayerGroup,
  FaPlus,
  FaMinus,
  FaExpand,
  FaLeaf,
  FaMap,
  FaTable,
} from "react-icons/fa";

import BasemapToggle from "./BasemapToggle";
import LayerToggle from "./LayerToggle";
import CarbonAnalysisPanel from "./CarbonAnalysisPanel";
import "../styles/NavigationBar.css";
import InfoBox from "./InfoBox";

const NavigationBar = ({
  map,
  boundariesMapRef,
  labelsMapRef,
  roadsMapRef,
  layersMapRef,
  resetView,
  onToggleVisibility,
  activeLayers,
  addLayer,
  removeLayer,
  resetLayers,
  basemapsConfig,
  currentBasemap,
  changeBasemap,
  forestLossRange,
  updateForestLossRange,
  deforestationRange,
  updateDeforestationRange,
  onOpacityChange,
  layersConfig,
  applyLayerFilter,
  updateBiomassYear,
  biomassYear,
  onToggleCarbonPanel,
  carbonPanelVisible,
  // New props for feature selection
  selectedFeature,
  selectedFeatureType,
  onFeatureSelect
}) => {
  const [isLayerToggleOpen, setIsLayerToggleOpen] = useState(false);
  const [isLayerToggleExpanded, setIsLayerToggleExpanded] = useState(true);
  const [isBasemapToggleOpen, setIsBasemapToggleOpen] = useState(false);
  const [isBasemapToggleExpanded, setIsBasemapToggleExpanded] = useState(true);
  const [infoBoxVisible, setInfoBoxVisible] = useState(false);
  const [isInfoBoxExpanded, setIsInfoBoxExpanded] = useState(true);
  // Add local carbon panel state if onToggleCarbonPanel isn't provided
  const [localCarbonPanelVisible, setLocalCarbonPanelVisible] = useState(false);

  // Animation states
  const [layerPanelAnimating, setLayerPanelAnimating] = useState(false);
  const [basemapPanelAnimating, setBasemapPanelAnimating] = useState(false);
  const [infoPanelAnimating, setInfoPanelAnimating] = useState(false);
  const [layerPanelClass, setLayerPanelClass] = useState('');
  const [basemapPanelClass, setBasemapPanelClass] = useState('');
  const [infoPanelClass, setInfoPanelClass] = useState('');

  // Refs for tracking timeout IDs to clean them up
  const layerAnimationTimeoutRef = useRef(null);
  const basemapAnimationTimeoutRef = useRef(null);
  const infoAnimationTimeoutRef = useRef(null);

  // Animation duration in ms (should match CSS transition duration)
  const ANIMATION_DURATION = 300;

  // Clean up timeouts on unmount
  useEffect(() => {
    return () => {
      if (layerAnimationTimeoutRef.current) clearTimeout(layerAnimationTimeoutRef.current);
      if (basemapAnimationTimeoutRef.current) clearTimeout(basemapAnimationTimeoutRef.current);
      if (infoAnimationTimeoutRef.current) clearTimeout(infoAnimationTimeoutRef.current);
    };
  }, []);

  // Auto-show InfoBox when a feature is selected
  useEffect(() => {
    if (selectedFeature && selectedFeatureType) {
      setInfoBoxVisible(true);
      setInfoPanelClass('panel-expanding');
      setIsInfoBoxExpanded(true);

      // Optionally collapse other panels when a feature is selected
      if (isLayerToggleOpen) setIsLayerToggleExpanded(false);
      if (isBasemapToggleOpen) setIsBasemapToggleExpanded(false);
    }
  }, [selectedFeature, selectedFeatureType, isLayerToggleOpen, isBasemapToggleOpen]);

  const handleLayerToggleExpandChange = (expanded) => {
    setIsLayerToggleExpanded(expanded);
  };

  const handleBasemapToggleExpandChange = (expanded) => {
    setIsBasemapToggleExpanded(expanded);
  };

  const handleInfoBoxExpandChange = (expanded) => {
    setIsInfoBoxExpanded(expanded);
  };

  const handleToggleInfoBox = () => {
    if (!infoBoxVisible) {
      setInfoBoxVisible(true);
      setInfoPanelClass('panel-expanding');
      setIsInfoBoxExpanded(true);
      if (isLayerToggleOpen) setIsLayerToggleExpanded(false);
      if (isBasemapToggleOpen) setIsBasemapToggleExpanded(false);
    } else {
      setInfoPanelAnimating(true);
      setInfoPanelClass('panel-collapsing');
      infoAnimationTimeoutRef.current = setTimeout(() => {
        setInfoBoxVisible(false);
        setInfoPanelAnimating(false);
        setInfoPanelClass('');
      }, ANIMATION_DURATION);
    }
  };

  const handleCloseInfoBox = () => {
    setInfoPanelAnimating(true);
    setInfoPanelClass('panel-collapsing');
    infoAnimationTimeoutRef.current = setTimeout(() => {
      setInfoBoxVisible(false);
      setInfoPanelAnimating(false);
      setInfoPanelClass('');

      // When closing the InfoBox, clear the feature selection if it was showing a feature
      if (selectedFeature && selectedFeatureType && onFeatureSelect) {
        onFeatureSelect(null, null);
      }
    }, ANIMATION_DURATION);
  };

  const zoomIn = () => {
    if (map) map.zoomIn();
  };

  const zoomOut = () => {
    if (map) map.zoomOut();
  };

  const toggleFullscreen = () => {
    const mapElement = document.getElementById("map");
    if (!document.fullscreenElement) {
      mapElement?.requestFullscreen().catch((err) => {
        alert(`Failed to enter fullscreen: ${err.message}`);
      });
    } else {
      document.exitFullscreen().catch((err) => {
        alert(`Failed to exit fullscreen: ${err.message}`);
      });
    }
  };

  const handleToggleLayerPanel = () => {
    if (!isLayerToggleOpen) {
      setIsLayerToggleOpen(true);
      setLayerPanelClass('panel-expanding');
      setIsLayerToggleExpanded(true);
      if (isBasemapToggleOpen) setIsBasemapToggleExpanded(false);
      if (infoBoxVisible) setIsInfoBoxExpanded(false);
    } else {
      setLayerPanelAnimating(true);
      setLayerPanelClass('panel-collapsing');
      layerAnimationTimeoutRef.current = setTimeout(() => {
        setIsLayerToggleOpen(false);
        setLayerPanelAnimating(false);
        setLayerPanelClass('');
      }, ANIMATION_DURATION);
    }
  };

  const handleToggleBasemapPanel = () => {
    if (!isBasemapToggleOpen) {
      setIsBasemapToggleOpen(true);
      setBasemapPanelClass('panel-expanding');
      setIsBasemapToggleExpanded(true);
      if (isLayerToggleOpen) setIsLayerToggleExpanded(false);
      if (infoBoxVisible) setIsInfoBoxExpanded(false);
    } else {
      setBasemapPanelAnimating(true);
      setBasemapPanelClass('panel-collapsing');
      basemapAnimationTimeoutRef.current = setTimeout(() => {
        setIsBasemapToggleOpen(false);
        setBasemapPanelAnimating(false);
        setBasemapPanelClass('');
      }, ANIMATION_DURATION);
    }
  };

  const handleCloseLayerToggle = () => {
    setLayerPanelAnimating(true);
    setLayerPanelClass('panel-collapsing');
    layerAnimationTimeoutRef.current = setTimeout(() => {
      setIsLayerToggleOpen(false);
      setLayerPanelAnimating(false);
      setLayerPanelClass('');
    }, ANIMATION_DURATION);
  };

  const handleCloseBasemapToggle = () => {
    setBasemapPanelAnimating(true);
    setBasemapPanelClass('panel-collapsing');
    basemapAnimationTimeoutRef.current = setTimeout(() => {
      setIsBasemapToggleOpen(false);
      setBasemapPanelAnimating(false);
      setBasemapPanelClass('');
    }, ANIMATION_DURATION);
  };

  const handleOpacityChange = (layerId, newOpacity) => {
    onOpacityChange(layerId, newOpacity);
  };

  const handleToggleCarbonPanel = () => {
    if (onToggleCarbonPanel) {
      onToggleCarbonPanel();
    } else {
      setLocalCarbonPanelVisible(!localCarbonPanelVisible);
    }
  };

  const handleCloseCarbonPanel = () => {
    if (onToggleCarbonPanel) {
      onToggleCarbonPanel();
    } else {
      setLocalCarbonPanelVisible(false);
    }
  };

  // Determine which carbon panel state to use
  const effectiveCarbonPanelVisible = onToggleCarbonPanel ? carbonPanelVisible : localCarbonPanelVisible;

  return (
    <>
      <div className="navigation-bar-container">
        <div className="nav-top-section">
          <div
            className="icon-container"
            onClick={toggleFullscreen}
            aria-label="Toggle Fullscreen"
          >
            <FaExpand className="nav-icon" title="Fullscreen" />
          </div>
          <div
            className={`icon-container ${isLayerToggleOpen ? "toggle-active" : ""}`}
            onClick={handleToggleLayerPanel}
            aria-label="Toggle Layers"
          >
            <FaLayerGroup className="nav-icon" title="Toggle Layers" />
          </div>
          <div
            className={`icon-container ${isBasemapToggleOpen ? "toggle-active" : ""}`}
            onClick={handleToggleBasemapPanel}
            aria-label="Toggle Basemap"
          >
            <FaMap className="nav-icon" title="Toggle Basemap" />
          </div>
          <div
            className={`icon-container ${infoBoxVisible ? "toggle-active" : ""}`}
            onClick={handleToggleInfoBox}
            aria-label="Toggle Info Box"
          >
            <FaTable className="nav-icon" title="Information" />
          </div>
        </div>

        <div className="nav-bottom-section">
          <div
            className="icon-container"
            onClick={resetView}
            aria-label="Reset View"
          >
            <FaHome className="nav-icon" title="Home" />
          </div>
          <div className="icon-container" onClick={zoomIn} aria-label="Zoom In">
            <FaPlus className="nav-icon" title="Zoom In" />
          </div>
          <div className="icon-container" onClick={zoomOut} aria-label="Zoom Out">
            <FaMinus className="nav-icon" title="Zoom Out" />
          </div>
        </div>
      </div>

      <div className="panels-container">
        <div
          className={`layer-toggle-overlay ${layerPanelClass}`}
          style={{
            zIndex: 999, // Highest z-index (topmost in stacking order)
            display: isLayerToggleOpen || layerPanelAnimating ? 'block' : 'none',
          }}
        >
          <LayerToggle
            layersConfig={layersConfig}
            activeLayers={activeLayers}
            addLayer={addLayer}
            removeLayer={removeLayer}
            resetLayers={resetLayers}
            forestLossRange={forestLossRange}
            updateForestLossRange={updateForestLossRange}
            deforestationRange={deforestationRange}
            updateDeforestationRange={updateDeforestationRange}
            onToggleVisibility={onToggleVisibility}
            onOpacityChange={handleOpacityChange}
            onClose={handleCloseLayerToggle}
            updateBiomassYear={updateBiomassYear}
            biomassYear={biomassYear}
            isOpen={isLayerToggleOpen}
            isExpanded={isLayerToggleExpanded}
            onExpandChange={handleLayerToggleExpandChange}
          />
        </div>

        <div
          className={`basemap-toggle-overlay ${basemapPanelClass}`}
          style={{
            zIndex: 998, // Middle z-index (between LayerToggle and InfoBox)
            display: isBasemapToggleOpen || basemapPanelAnimating ? 'block' : 'none',
            minHeight: isBasemapToggleExpanded ? 'auto' : '60px', // Match collapsed height
          }}
        >
          <BasemapToggle
            basemapsConfig={basemapsConfig}
            currentBasemap={currentBasemap}
            changeBasemap={changeBasemap}
            map={map}
            labelsMap={labelsMapRef?.current || null}
            boundariesMap={boundariesMapRef?.current || null}
            roadsMap={roadsMapRef?.current || null}
            layersMapRef={layersMapRef}
            isOpen={isBasemapToggleOpen}
            isExpanded={isBasemapToggleExpanded}
            onToggle={handleToggleBasemapPanel}
            onClose={handleCloseBasemapToggle}
            onExpandChange={handleBasemapToggleExpandChange}
          />
        </div>

        <div
          className={`info-box-overlay ${infoPanelClass}`}
          style={{
            zIndex: 997, // Lowest z-index (bottom in stacking order)
            display: infoBoxVisible || infoPanelAnimating ? 'block' : 'none',
          }}
        >
          <InfoBox
            selectedFeature={selectedFeature || {
              properties: {
                name: "Map Information",
                description: "This map shows geographic features of Sarawak.",
                instructions: "Click on map elements to see their details.",
              },
            }}
            featureType={selectedFeatureType || "Properties"}
            onClose={handleCloseInfoBox}
            isOpen={infoBoxVisible}
            isExpanded={isInfoBoxExpanded}
            onExpandChange={handleInfoBoxExpandChange}
          />
        </div>
      </div>

      <div
        className={`icon-container carbon-toggle-button ${effectiveCarbonPanelVisible ? "toggle-active" : ""}`}
        onClick={handleToggleCarbonPanel}
        aria-label="Toggle Carbon Analysis"
        style={{
          position: "fixed",
          top: "1%",
          right: "10px",
          zIndex: 999, // Higher than panels to stay on top
        }}
      >
        <FaLeaf className="nav-icon" title="Carbon Analysis" />
      </div>

      {!onToggleCarbonPanel && localCarbonPanelVisible && (
        <CarbonAnalysisPanel map={map} onClose={handleCloseCarbonPanel} />
      )}
    </>
  );
};

NavigationBar.propTypes = {
  map: PropTypes.object,
  boundariesMapRef: PropTypes.object,
  labelsMapRef: PropTypes.object,
  roadsMapRef: PropTypes.object,
  layersMapRef: PropTypes.object,
  resetView: PropTypes.func.isRequired,
  onToggleVisibility: PropTypes.func.isRequired,
  activeLayers: PropTypes.array.isRequired,
  addLayer: PropTypes.func.isRequired,
  removeLayer: PropTypes.func.isRequired,
  resetLayers: PropTypes.func.isRequired,
  basemapsConfig: PropTypes.array.isRequired,
  currentBasemap: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    style: PropTypes.string.isRequired,
  }).isRequired,
  changeBasemap: PropTypes.func.isRequired,
  forestLossRange: PropTypes.shape({
    start: PropTypes.number.isRequired,
    end: PropTypes.number.isRequired,
  }).isRequired,
  updateForestLossRange: PropTypes.func.isRequired,
  deforestationRange: PropTypes.shape({
    startDate: PropTypes.string,
    endDate: PropTypes.string,
  }),
  updateDeforestationRange: PropTypes.func,
  onOpacityChange: PropTypes.func.isRequired,
  layersConfig: PropTypes.array.isRequired,
  applyLayerFilter: PropTypes.func,
  updateBiomassYear: PropTypes.func,
  biomassYear: PropTypes.number,
  onToggleCarbonPanel: PropTypes.func,
  carbonPanelVisible: PropTypes.bool,
  // New prop types for feature selection
  selectedFeature: PropTypes.object,
  selectedFeatureType: PropTypes.string,
  onFeatureSelect: PropTypes.func
};

NavigationBar.defaultProps = {
  map: null,
  boundariesMapRef: null,
  labelsMapRef: null,
  roadsMapRef: null,
  layersMapRef: null,
  applyLayerFilter: null,
  updateBiomassYear: null,
  biomassYear: 2020,
  onToggleCarbonPanel: null,
  carbonPanelVisible: false,
  // Default values for feature selection
  selectedFeature: null,
  selectedFeatureType: null,
  onFeatureSelect: null
};

export default NavigationBar;
